/* ============================================================================
   MEMORA WORD (.DOCX) IMPORT
   Word files enter through the same conversion step as pictures
   (convertMemoraImageSourceFiles in app.core.js), so every import path - the
   rail Upload, the Import modal, desktop drops - takes them without a branch
   of its own:
   - a document that reads as a multiple-choice question bank becomes a quiz
     JSON file for the native staged question import;
   - any other document, and a bank whose questions lack a recognizable answer,
     becomes a text PDF, so the viewer, Ask, Study Guide, quiz generation and
     Lecture Finder take it exactly as they take a lecture.
   Mammoth (vendored, loaded on first use) reads the document. Its HTML is
   parsed into an inert DOMParser document and walked; it is never inserted
   into the page.
   ============================================================================ */

const MEMORA_DOCX_MAMMOTH_SRC = 'js/vendor/mammoth/1.13.0/mammoth.browser.min.js';
const MEMORA_DOCX_FONT_BASE = 'js/vendor/pdfjs/4.10.38/standard_fonts/';
const MEMORA_DOCX_FONT_FAMILY = 'MemoraDocSans';
const MEMORA_DOCX_FONT_FILES = Object.freeze({
    normal: 'LiberationSans-Regular.ttf',
    bold: 'LiberationSans-Bold.ttf',
    italic: 'LiberationSans-Italic.ttf',
    bolditalic: 'LiberationSans-BoldItalic.ttf'
});
const MEMORA_DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const MEMORA_DOCX_WORD_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
const MEMORA_DOCX_MIN_BANK_QUESTIONS = 3;
const MEMORA_DOCX_MAX_CHOICES = 8;
const MEMORA_DOCX_IMAGE_MAX_SIDE_PX = 1600;
// Underline is ignored by Mammoth's default map and highlight has no default; both can mark answers.
const MEMORA_DOCX_STYLE_MAP = Object.freeze(['u => u', 'highlight => mark', 'strike => s']);
const MEMORA_DOCX_ANSWER_SIGNALS = Object.freeze(['star', 'color', 'mark', 'u', 'bold']);
const MEMORA_DOCX_QUESTION_CUE_RE = /\?\s*$|_{3,}|\bexcept\b|all of the following|which of|most likely|^\s*(?:which|what|how|why|when|where|who|whom|identify|select|choose|name)\b/i;
const MEMORA_DOCX_MAX_STEM_CHARS = 900;

let memoraDocxFontDataPromise = null;

function isMemoraDocxFile(file) {
    const name = String(file?.name || '');
    return /\.docx$/i.test(name)
        || (String(file?.type || '').toLowerCase() === MEMORA_DOCX_MIME && !/\.[a-z0-9]+$/i.test(name));
}

function isMemoraLegacyWordFile(file) {
    return /\.doc$/i.test(String(file?.name || ''));
}

function normalizeMemoraDocxText(text) {
    return String(text || '').replace(/[ \t]/g, ' ').replace(/\s+/g, ' ').trim();
}

function normalizeMemoraDocxMatchKey(text) {
    return normalizeMemoraDocxText(text).toLowerCase();
}

/* ---------- Reading the document ---------- */

async function loadMemoraDocxMammoth() {
    if (window.mammoth?.convertToHtml) return window.mammoth;
    const runtime = window.MemoraRuntime;
    if (typeof runtime?.ensureScriptLoaded !== 'function') throw new Error('The Word reader is unavailable.');
    await runtime.ensureScriptLoaded({
        src: MEMORA_DOCX_MAMMOTH_SRC,
        key: 'memora-docx-mammoth',
        test: () => window.mammoth?.convertToHtml
    });
    if (!window.mammoth?.convertToHtml) throw new Error('The Word reader did not load.');
    return window.mammoth;
}

// Reads one stored or deflated entry from the .docx zip. Mammoth exposes no run colors,
// so word/document.xml is read directly for that single signal.
async function readMemoraDocxZipEntryText(buffer, entryPath) {
    try {
        const view = new DataView(buffer);
        let eocd = -1;
        for (let index = buffer.byteLength - 22; index >= Math.max(0, buffer.byteLength - 65557); index -= 1) {
            if (view.getUint32(index, true) === 0x06054b50) { eocd = index; break; }
        }
        if (eocd < 0) return '';
        const count = view.getUint16(eocd + 10, true);
        let offset = view.getUint32(eocd + 16, true);
        const decoder = new TextDecoder();
        for (let entry = 0; entry < count; entry += 1) {
            if (view.getUint32(offset, true) !== 0x02014b50) return '';
            const method = view.getUint16(offset + 10, true);
            const compressedSize = view.getUint32(offset + 20, true);
            const nameLength = view.getUint16(offset + 28, true);
            const extraLength = view.getUint16(offset + 30, true);
            const commentLength = view.getUint16(offset + 32, true);
            const localOffset = view.getUint32(offset + 42, true);
            const name = decoder.decode(new Uint8Array(buffer, offset + 46, nameLength));
            if (name === entryPath) {
                const start = localOffset + 30 + view.getUint16(localOffset + 26, true) + view.getUint16(localOffset + 28, true);
                const data = new Uint8Array(buffer, start, compressedSize);
                if (method === 0) return decoder.decode(data);
                if (method !== 8 || typeof DecompressionStream !== 'function') return '';
                const stream = new Blob([data]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
                return await new Response(stream).text();
            }
            offset += 46 + nameLength + extraLength + commentLength;
        }
    } catch (error) {
        console.warn('Word color read skipped:', error);
    }
    return '';
}

function isMemoraDocxAnswerColor(value) {
    const color = String(value || '').trim().toLowerCase();
    if (!/^[0-9a-f]{6}$/.test(color)) return false;
    const [r, g, b] = [0, 2, 4].map((index) => parseInt(color.slice(index, index + 2), 16));
    // Near-black and grey text is body text, not a marked answer.
    return Math.max(r, g, b) - Math.min(r, g, b) > 60;
}

// normalized paragraph text -> share of its characters set in a non-body color.
function buildMemoraDocxColorMap(documentXml) {
    const map = new Map();
    if (!documentXml || typeof DOMParser !== 'function') return map;
    const xml = new DOMParser().parseFromString(documentXml, 'application/xml');
    Array.from(xml.getElementsByTagNameNS(MEMORA_DOCX_WORD_NS, 'p')).forEach((paragraph) => {
        let total = 0;
        let colored = 0;
        let text = '';
        Array.from(paragraph.getElementsByTagNameNS(MEMORA_DOCX_WORD_NS, 'r')).forEach((run) => {
            let runText = '';
            Array.from(run.childNodes).forEach((node) => {
                if (node.localName === 't') runText += node.textContent || '';
                else if (node.localName === 'tab') runText += ' ';
            });
            if (!runText) return;
            text += runText;
            const letters = runText.replace(/\s/g, '').length;
            total += letters;
            const colorNode = run.getElementsByTagNameNS(MEMORA_DOCX_WORD_NS, 'color')[0];
            if (colorNode && isMemoraDocxAnswerColor(colorNode.getAttributeNS(MEMORA_DOCX_WORD_NS, 'val') || colorNode.getAttribute('w:val'))) {
                colored += letters;
            }
        });
        if (total && colored) map.set(normalizeMemoraDocxMatchKey(text), colored / total);
    });
    return map;
}

async function readMemoraDocxDocument(file) {
    const buffer = await file.arrayBuffer();
    const mammoth = await loadMemoraDocxMammoth();
    const [result, documentXml] = await Promise.all([
        mammoth.convertToHtml({ arrayBuffer: buffer }, { styleMap: MEMORA_DOCX_STYLE_MAP.slice() }),
        readMemoraDocxZipEntryText(buffer, 'word/document.xml')
    ]);
    const html = new DOMParser().parseFromString(`<!doctype html><body>${result?.value || ''}</body>`, 'text/html');
    return { body: html.body, colorMap: buildMemoraDocxColorMap(documentXml) };
}

/* ---------- Blocks: one walk feeds both the question parser and the PDF ---------- */

function collectMemoraDocxRuns(node, style = {}, runs = []) {
    node.childNodes.forEach((child) => {
        if (child.nodeType === 3) {
            if (child.textContent) runs.push({ text: child.textContent, ...style });
            return;
        }
        if (child.nodeType !== 1) return;
        const tag = child.tagName.toLowerCase();
        if (tag === 'ol' || tag === 'ul' || tag === 'table' || tag === 'img') return;
        if (tag === 'br') { runs.push({ text: '\n', ...style }); return; }
        const next = { ...style };
        if (tag === 'strong' || tag === 'b') next.bold = true;
        if (tag === 'em' || tag === 'i') next.italic = true;
        if (tag === 'u') next.u = true;
        if (tag === 'mark') next.mark = true;
        if (tag === 's' || tag === 'del') next.strike = true;
        if (tag === 'sup') next.sup = true;
        if (tag === 'sub') next.sub = true;
        collectMemoraDocxRuns(child, next, runs);
    });
    return runs;
}

// A table cell keeps its paragraphs and list items on separate lines.
function collectMemoraDocxCellRuns(cell, depth = 0, runs = []) {
    const breakLine = () => { if (runs.length && runs[runs.length - 1].text !== '\n') runs.push({ text: '\n' }); };
    if (!cell.children.length) return collectMemoraDocxRuns(cell, {}, runs);
    Array.from(cell.children).forEach((child) => {
        const tag = child.tagName.toLowerCase();
        if (tag === 'ol' || tag === 'ul') {
            let index = 0;
            Array.from(child.children).forEach((item) => {
                index += 1;
                breakLine();
                runs.push({ text: `${'  '.repeat(depth)}${getMemoraDocxListMarker(tag, depth, index)} ` });
                collectMemoraDocxRuns(item, {}, runs);
                Array.from(item.children).forEach((nested) => {
                    if (/^(?:ol|ul)$/i.test(nested.tagName)) collectMemoraDocxCellRuns({ children: [nested] }, depth + 1, runs);
                });
            });
        } else if (tag !== 'table') {
            breakLine();
            collectMemoraDocxRuns(child, {}, runs);
        }
    });
    return runs;
}

function getMemoraDocxListMarker(kind, depth, index) {
    if (kind === 'ul') return depth === 0 ? '•' : '–';
    if (depth % 2 === 1) return `${String.fromCharCode(97 + ((index - 1) % 26))}.`;
    return `${index}.`;
}

function collectMemoraDocxBlocks(root) {
    const blocks = [];
    const pushImages = (element) => {
        element.querySelectorAll('img').forEach((image) => {
            // Only images that belong to this element, not to a nested list item.
            const owner = image.closest('li');
            if (owner && owner !== element) return;
            const src = image.getAttribute('src') || '';
            if (src.startsWith('data:')) blocks.push({ type: 'image', src, alt: image.getAttribute('alt') || '' });
        });
    };
    const walkList = (list, depth) => {
        const kind = list.tagName.toLowerCase();
        let index = 0;
        Array.from(list.children).forEach((item) => {
            if (item.tagName.toLowerCase() !== 'li') return;
            index += 1;
            const runs = collectMemoraDocxRuns(item);
            blocks.push({ type: 'item', runs, kind, depth, index, marker: getMemoraDocxListMarker(kind, depth, index) });
            pushImages(item);
            Array.from(item.children).forEach((child) => {
                const tag = child.tagName.toLowerCase();
                if (tag === 'ol' || tag === 'ul') walkList(child, depth + 1);
            });
        });
    };
    Array.from(root.children).forEach((element) => {
        const tag = element.tagName.toLowerCase();
        if (/^h[1-6]$/.test(tag)) {
            blocks.push({ type: 'heading', level: Number(tag[1]), runs: collectMemoraDocxRuns(element) });
        } else if (tag === 'p') {
            const runs = collectMemoraDocxRuns(element);
            if (normalizeMemoraDocxText(runs.map((run) => run.text).join(''))) blocks.push({ type: 'para', runs });
            pushImages(element);
        } else if (tag === 'ol' || tag === 'ul') {
            walkList(element, 0);
        } else if (tag === 'table') {
            const rows = Array.from(element.querySelectorAll(':scope > tbody > tr, :scope > thead > tr, :scope > tr')).map((row) => (
                Array.from(row.children).map((cell) => collectMemoraDocxCellRuns(cell))
            )).filter((row) => row.length);
            if (rows.length) blocks.push({ type: 'table', rows });
        } else if (tag === 'hr') {
            blocks.push({ type: 'rule' });
        } else {
            const runs = collectMemoraDocxRuns(element);
            if (normalizeMemoraDocxText(runs.map((run) => run.text).join(''))) blocks.push({ type: 'para', runs });
            pushImages(element);
        }
    });
    return blocks;
}

function getMemoraDocxRunsText(runs) {
    return (runs || []).map((run) => run.text).join('');
}

function getMemoraDocxStyleShare(runs, key) {
    let total = 0;
    let styled = 0;
    (runs || []).forEach((run) => {
        const letters = String(run.text || '').replace(/\s/g, '').length;
        total += letters;
        if (run[key]) styled += letters;
    });
    return total ? styled / total : 0;
}

// Flattens blocks into the line records the question parser reads.
function buildMemoraDocxLines(blocks, colorMap = new Map()) {
    const lines = [];
    const pushLine = (runs, extra = {}) => {
        String(getMemoraDocxRunsText(runs)).split('\n').forEach((part, position, parts) => {
            const text = normalizeMemoraDocxText(part);
            if (!text) return;
            const partRuns = parts.length > 1 ? [{ text: part }] : runs;
            lines.push({
                text,
                bold: getMemoraDocxStyleShare(partRuns, 'bold'),
                u: getMemoraDocxStyleShare(partRuns, 'u'),
                mark: getMemoraDocxStyleShare(partRuns, 'mark'),
                color: colorMap.get(normalizeMemoraDocxMatchKey(text)) || 0,
                ...extra
            });
        });
    };
    blocks.forEach((block) => {
        if (block.type === 'heading') pushLine(block.runs, { heading: true });
        else if (block.type === 'para') pushLine(block.runs);
        else if (block.type === 'item') pushLine(block.runs, { list: block.kind, depth: block.depth, index: block.index });
        else if (block.type === 'image') lines.push({ text: '', image: true });
        else if (block.type === 'table') {
            block.rows.forEach((row) => {
                const cells = row.map((cell) => normalizeMemoraDocxText(getMemoraDocxRunsText(cell)));
                // A short row ("12 | B") is an answer-key line; a longer one is content.
                if (cells.every((cell) => cell.length <= 12)) {
                    const text = cells.filter(Boolean).join(' ');
                    if (text) lines.push({ text, bold: 0, u: 0, mark: 0, color: 0, tableRow: true });
                } else {
                    row.forEach((cell) => pushLine(cell));
                }
            });
        }
    });
    return lines;
}

/* ---------- Question bank parsing ---------- */

const MEMORA_DOCX_CHOICE_RE = /^\(?([A-Ha-h])\s*[.):]\s+(\S.*)$/;
const MEMORA_DOCX_NUMBERED_RE = /^(?:q(?:uestion)?\.?\s*)?(\d{1,3})\s*[.):]\s*(\S.*)$/i;
const MEMORA_DOCX_ANSWER_RE = /^(?:correct\s+)?(?:answer|ans)\b\.?\s*(?:is\s*)?[:\-–=]?\s*(.+)$/i;
const MEMORA_DOCX_EXPLANATION_RE = /^(?:explanation|rationale|reason(?:ing)?|why|notes?)\s*[:\-–]\s*(.*)$/i;
const MEMORA_DOCX_KEY_HEADING_RE = /^(?:answer\s*key|answers?|key)\s*:?$/i;
const MEMORA_DOCX_KEY_PAIR_RE = /(\d{1,3})\s*[.):\-–]?\s*\(?([A-Ha-h])\)?(?=\s|,|;|$)/g;
const MEMORA_DOCX_STAR_RE = /^\s*[*✓✔]\s*|\s*(?:[*✓✔]|\((?:correct|answer|ans)\))\s*$/i;

function isMemoraDocxKeyLine(text) {
    const value = String(text || '');
    const pairs = value.match(MEMORA_DOCX_KEY_PAIR_RE) || [];
    return pairs.length > 0 && !value.replace(MEMORA_DOCX_KEY_PAIR_RE, '').replace(/[\s,;|]/g, '');
}

function readMemoraDocxKeyPairs(text, keyMap) {
    MEMORA_DOCX_KEY_PAIR_RE.lastIndex = 0;
    let match = MEMORA_DOCX_KEY_PAIR_RE.exec(text);
    while (match) {
        keyMap.set(Number(match[1]), match[2].toUpperCase());
        match = MEMORA_DOCX_KEY_PAIR_RE.exec(text);
    }
    MEMORA_DOCX_KEY_PAIR_RE.lastIndex = 0;
}

function looksLikeMemoraDocxStem(text) {
    const value = String(text || '');
    return /[?:]\s*$|_{3,}/.test(value)
        || /^\W*(?:which|what|how|why|when|where|who|whom)\b/i.test(value)
        || /\bexcept\b|all of the following|which of/i.test(value);
}

function resolveMemoraDocxAnswerHint(hint, choices) {
    const text = normalizeMemoraDocxText(hint).replace(/[.;]+$/, '');
    const labelIndex = (value) => {
        const index = value.toUpperCase().charCodeAt(0) - 65;
        return index < choices.length ? index : -1;
    };
    const keys = choices.map((choice) => normalizeMemoraDocxMatchKey(choice.text));
    const uniqueMatch = (key) => {
        const hits = keys.map((choiceKey, index) => (choiceKey === key ? index : -1)).filter((index) => index >= 0);
        return hits.length === 1 ? hits[0] : -1;
    };
    // A bare label: "B", "B.", "(B)", "B)".
    const bare = text.match(/^\(?([A-Ha-h])\)?[.):]?$/);
    if (bare) return labelIndex(bare[1]);
    // Full choice text comes before a leading letter, so "A positive" or "B cells" keeps its identity.
    const whole = normalizeMemoraDocxMatchKey(text);
    if (!whole) return -1;
    const wholeMatch = uniqueMatch(whole);
    if (wholeMatch >= 0) return wholeMatch;
    // An explicitly punctuated label: "B. A positive", "(B) A positive", "B) ...", "B - ...".
    const labelled = text.match(/^\(?([A-Ha-h])\)?\s*[.):\-–—]\s*(.*)$/) || text.match(/^\(([A-Ha-h])\)\s*(.*)$/);
    if (labelled && labelIndex(labelled[1]) >= 0) {
        const index = labelIndex(labelled[1]);
        // Text after the label must not name a different choice: "B. Cephalexin" when
        // Cephalexin is A is a contradiction, not a key, and is left unresolved.
        const rest = normalizeMemoraDocxMatchKey(labelled[2] || '');
        if (rest && keys[index] !== rest && keys.includes(rest)) return -1;
        return index;
    }
    // "B A positive": a letter and a space are a label when the rest names that same choice.
    const spaced = text.match(/^([A-Ha-h])\s+(.+)$/);
    if (spaced && labelIndex(spaced[1]) >= 0 && keys[labelIndex(spaced[1])] === normalizeMemoraDocxMatchKey(spaced[2])) return labelIndex(spaced[1]);
    // Text naming more than one choice exactly is ambiguous; the first is not the answer.
    if (keys.includes(whole)) return -1;
    const partial = keys.map((choiceKey, index) => (choiceKey.includes(whole) ? index : -1)).filter((index) => index >= 0);
    if (partial.length === 1) return partial[0];
    // Last resort, as before: a leading letter and a space read as that label ("A because ...").
    return spaced ? labelIndex(spaced[1]) : -1;
}

// The one choice a formatting signal marks; ambiguous or absent marks give no answer.
function findMemoraDocxMarkedChoice(choices) {
    for (const signal of MEMORA_DOCX_ANSWER_SIGNALS) {
        const marked = choices.map((choice, index) => ((signal === 'star' ? choice.star : choice[signal] >= 0.6) ? index : -1)).filter((index) => index >= 0);
        if (marked.length === 1) return marked[0];
        if (marked.length > 1 && signal === 'star') return -1;
    }
    return -1;
}

function parseMemoraDocxQuestionLines(lines) {
    const questions = [];
    const keyMap = new Map();
    let current = null;
    let keyMode = false;

    const finish = () => {
        if (current && current.stem.length && current.choices.length >= 2 && current.choices.length <= MEMORA_DOCX_MAX_CHOICES) {
            current.ordinal = questions.length + 1;
            questions.push(current);
        }
        current = null;
    };
    const start = (line) => {
        finish();
        const numbered = line.list ? null : line.text.match(MEMORA_DOCX_NUMBERED_RE);
        current = {
            number: numbered ? Number(numbered[1]) : (line.list === 'ol' && !line.depth ? line.index : null),
            stem: [(numbered ? numbered[2] : line.text).replace(/^[\s.):\]-]+/, '')],
            stemListDepth: line.list ? line.depth : null,
            choices: [],
            hint: '',
            explanation: [],
            afterAnswer: false,
            hasImage: false
        };
    };
    const asChoice = (line) => {
        if (!current || current.afterAnswer) return null;
        const typed = line.list ? null : line.text.match(MEMORA_DOCX_CHOICE_RE);
        let text = '';
        if (typed) {
            const expected = String.fromCharCode(65 + current.choices.length);
            if (typed[1].toUpperCase() !== expected) return null;
            text = typed[2];
        } else if (line.list && (current.stemListDepth === null || line.depth > current.stemListDepth)) {
            text = line.text;
        } else if (line.list && line.depth === current.stemListDepth && (current.flat || !current.choices.length) && !looksLikeMemoraDocxStem(line.text)) {
            // Word often numbers a stem and its choices in one flat list; a non-question item is a choice.
            current.flat = true;
            text = line.text;
        } else {
            return null;
        }
        const star = MEMORA_DOCX_STAR_RE.test(text);
        return {
            text: text.replace(MEMORA_DOCX_STAR_RE, '').trim(),
            star,
            bold: line.bold,
            u: line.u,
            mark: line.mark,
            color: line.color
        };
    };

    for (const line of lines) {
        if (line.image) { if (current) current.hasImage = true; continue; }
        const text = line.text;
        if (MEMORA_DOCX_KEY_HEADING_RE.test(text)) { finish(); keyMode = true; continue; }
        if (isMemoraDocxKeyLine(text) && (keyMode || line.tableRow || !current || current.choices.length >= 2)) {
            finish();
            readMemoraDocxKeyPairs(text, keyMap);
            keyMode = true;
            continue;
        }
        if (keyMode) {
            if (text.length < 4) continue;
            keyMode = false;
        }
        const answer = text.match(MEMORA_DOCX_ANSWER_RE);
        if (answer && current && current.choices.length >= 2) {
            current.hint = answer[1];
            current.afterAnswer = true;
            continue;
        }
        const explanation = text.match(MEMORA_DOCX_EXPLANATION_RE);
        if (explanation && current && current.choices.length >= 2) {
            if (explanation[1]) current.explanation.push(explanation[1]);
            current.afterAnswer = true;
            continue;
        }
        const choice = asChoice(line);
        if (choice) { current.choices.push(choice); continue; }
        const looksNew = MEMORA_DOCX_NUMBERED_RE.test(text) || /\?\s*$/.test(text) || line.heading || (line.list && !line.depth);
        if (current && current.afterAnswer && !looksNew) { current.explanation.push(text); continue; }
        if (current && !current.choices.length && !current.afterAnswer && !MEMORA_DOCX_NUMBERED_RE.test(text) && !line.heading) {
            current.stem.push(text);
            continue;
        }
        start(line);
    }
    finish();

    return questions.map((question) => {
        let answer = question.hint ? resolveMemoraDocxAnswerHint(question.hint, question.choices) : -1;
        const keyed = keyMap.get(question.number ?? question.ordinal);
        if (answer < 0 && keyed) {
            const index = keyed.charCodeAt(0) - 65;
            if (index < question.choices.length) answer = index;
        }
        if (answer < 0) answer = findMemoraDocxMarkedChoice(question.choices);
        return {
            q: question.stem.join(' '),
            c: question.choices.map((choice) => choice.text),
            a: answer,
            e: question.explanation.join(' ').trim(),
            hasImage: question.hasImage
        };
    });
}

function isMemoraDocxQuestionBank(questions) {
    const list = Array.isArray(questions) ? questions : [];
    if (list.length < MEMORA_DOCX_MIN_BANK_QUESTIONS) return false;
    const cued = list.filter((question) => question.q.length <= MEMORA_DOCX_MAX_STEM_CHARS && MEMORA_DOCX_QUESTION_CUE_RE.test(question.q)).length;
    const answered = list.filter((question) => question.a >= 0).length;
    // Notes outlines also look like stems with lettered sub-points; they rarely read as questions.
    return cued >= Math.max(MEMORA_DOCX_MIN_BANK_QUESTIONS, Math.ceil(list.length * 0.5))
        || (answered >= Math.max(MEMORA_DOCX_MIN_BANK_QUESTIONS, Math.ceil(list.length * 0.6)) && cued >= Math.ceil(list.length * 0.25));
}

/* ---------- PDF output ---------- */

function encodeMemoraDocxBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let index = 0; index < bytes.length; index += 0x8000) {
        binary += String.fromCharCode.apply(null, bytes.subarray(index, index + 0x8000));
    }
    return btoa(binary);
}

function loadMemoraDocxFontData() {
    if (!memoraDocxFontDataPromise) {
        memoraDocxFontDataPromise = Promise.all(Object.entries(MEMORA_DOCX_FONT_FILES).map(async ([style, file]) => {
            const response = await fetch(new URL(MEMORA_DOCX_FONT_BASE + file, document.baseURI).href);
            if (!response.ok) throw new Error(`Font ${file} did not load.`);
            return [style, file, encodeMemoraDocxBase64(await response.arrayBuffer())];
        })).catch((error) => {
            memoraDocxFontDataPromise = null;
            throw error;
        });
    }
    return memoraDocxFontDataPromise;
}

async function prepareMemoraDocxImage(src) {
    const blob = await (await fetch(src)).blob();
    const bitmap = await createImageBitmap(blob);
    try {
        const scale = Math.min(1, MEMORA_DOCX_IMAGE_MAX_SIDE_PX / Math.max(bitmap.width, bitmap.height, 1));
        const width = Math.max(1, Math.round(bitmap.width * scale));
        const height = Math.max(1, Math.round(bitmap.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d');
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, width, height);
        context.drawImage(bitmap, 0, 0, width, height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.86);
        canvas.width = 0;
        canvas.height = 0;
        return { dataUrl, width: bitmap.width, height: bitmap.height };
    } finally {
        bitmap.close?.();
    }
}

function createMemoraDocxPdfWriter(doc) {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 54;
    const contentWidth = pageWidth - margin * 2;
    let y = margin;

    const setStyle = (run, size) => {
        const style = run.bold && run.italic ? 'bolditalic' : run.bold ? 'bold' : run.italic ? 'italic' : 'normal';
        doc.setFont(MEMORA_DOCX_FONT_FAMILY, style);
        doc.setFontSize(run.sup || run.sub ? size * 0.7 : size);
    };
    const ensure = (height) => {
        if (y + height > pageHeight - margin && y > margin) {
            doc.addPage();
            y = margin;
        }
    };
    // Word-wraps styled runs into lines of measured segments.
    const layout = (runs, width, size) => {
        const lines = [];
        let line = [];
        let lineWidth = 0;
        const flush = () => { lines.push(line); line = []; lineWidth = 0; };
        (runs || []).forEach((run) => {
            String(run.text || '').replace(/[\t ]/g, ' ').split(/(\n| +)/).forEach((token) => {
                if (!token) return;
                if (token === '\n') { flush(); return; }
                const isSpace = /^ +$/.test(token);
                if (isSpace && !line.length) return;
                setStyle(run, size);
                let piece = isSpace ? ' ' : token;
                let pieceWidth = doc.getTextWidth(piece);
                if (!isSpace && lineWidth + pieceWidth > width && line.length) {
                    while (line.length && /^ +$/.test(line[line.length - 1].text)) line.pop();
                    flush();
                }
                // A single word wider than the line breaks by characters.
                while (!isSpace && pieceWidth > width && piece.length > 1) {
                    let cut = piece.length - 1;
                    while (cut > 1 && doc.getTextWidth(piece.slice(0, cut)) > width - lineWidth) cut -= 1;
                    line.push({ text: piece.slice(0, cut), run, width: doc.getTextWidth(piece.slice(0, cut)) });
                    flush();
                    piece = piece.slice(cut);
                    pieceWidth = doc.getTextWidth(piece);
                }
                line.push({ text: piece, run, width: pieceWidth });
                lineWidth += pieceWidth;
            });
        });
        if (line.length) flush();
        return lines.length ? lines : [[]];
    };
    const drawLines = (lines, x, size, lineHeight) => {
        lines.forEach((segments) => {
            ensure(lineHeight);
            let cursor = x;
            const baseline = y + size * 0.82;
            segments.forEach((segment) => {
                const { run } = segment;
                if (run.mark) {
                    doc.setFillColor(255, 236, 140);
                    doc.rect(cursor, y + 1, segment.width, lineHeight - 2, 'F');
                }
                setStyle(run, size);
                doc.setTextColor(20, 20, 20);
                const shift = run.sup ? -size * 0.32 : run.sub ? size * 0.18 : 0;
                doc.text(segment.text, cursor, baseline + shift);
                if (run.u || run.strike) {
                    doc.setDrawColor(40, 40, 40);
                    doc.setLineWidth(0.6);
                    const lineY = run.strike ? baseline - size * 0.3 : baseline + 1.4;
                    doc.line(cursor, lineY, cursor + segment.width, lineY);
                }
                cursor += segment.width;
            });
            y += lineHeight;
        });
    };

    return {
        paragraph(runs, { size = 11, indent = 0, marker = '', before = 0, after = 6, bold = false } = {}) {
            const styled = bold ? runs.map((run) => ({ ...run, bold: true })) : runs;
            const markerWidth = marker ? 18 : 0;
            const lines = layout(styled, contentWidth - indent - markerWidth, size);
            const lineHeight = size * 1.38;
            y += before;
            ensure(lineHeight);
            if (marker) {
                setStyle({}, size);
                doc.setTextColor(20, 20, 20);
                doc.text(marker, margin + indent, y + size * 0.82);
            }
            drawLines(lines, margin + indent + markerWidth, size, lineHeight);
            y += after;
        },
        async image(src) {
            try {
                const prepared = await prepareMemoraDocxImage(src);
                let width = prepared.width * 0.75;
                let height = prepared.height * 0.75;
                const maxHeight = (pageHeight - margin * 2) * 0.85;
                const scale = Math.min(1, contentWidth / width, maxHeight / height);
                width *= scale;
                height *= scale;
                ensure(height + 8);
                doc.addImage(prepared.dataUrl, 'JPEG', margin, y, width, height);
                y += height + 10;
            } catch (_error) {
                this.paragraph([{ text: '[An image in this document could not be shown.]', italic: true }], { size: 9.5 });
            }
        },
        table(rows) {
            const columns = Math.max(...rows.map((row) => row.length));
            const lengths = Array.from({ length: columns }, (_, column) => Math.max(4, ...rows.map((row) => normalizeMemoraDocxText(getMemoraDocxRunsText(row[column] || [])).length)));
            const weights = lengths.map((length) => Math.sqrt(Math.min(length, 400)));
            const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
            const widths = weights.map((weight) => Math.max(contentWidth * 0.1, (contentWidth * weight) / totalWeight));
            const scale = contentWidth / widths.reduce((sum, width) => sum + width, 0);
            const finalWidths = widths.map((width) => width * scale);
            const size = 9.5;
            const lineHeight = size * 1.32;
            const pad = 4;
            y += 4;
            rows.forEach((row) => {
                const cellLines = finalWidths.map((width, column) => layout(row[column] || [], width - pad * 2, size));
                const height = Math.max(...cellLines.map((lines) => lines.length)) * lineHeight + pad * 2;
                ensure(Math.min(height, pageHeight - margin * 2));
                const top = y;
                let x = margin;
                cellLines.forEach((lines, column) => {
                    doc.setDrawColor(150, 150, 150);
                    doc.setLineWidth(0.5);
                    doc.rect(x, top, finalWidths[column], height);
                    y = top + pad;
                    drawLines(lines, x + pad, size, lineHeight);
                    x += finalWidths[column];
                });
                y = top + height;
            });
            y += 8;
        },
        rule() {
            ensure(12);
            doc.setDrawColor(170, 170, 170);
            doc.setLineWidth(0.6);
            doc.line(margin, y + 5, margin + contentWidth, y + 5);
            y += 12;
        }
    };
}

async function renderMemoraDocxPdf(blocks, title, pdfName, lastModified) {
    const jsPDF = await getMemoraJsPdfConstructor();
    const doc = new jsPDF({ unit: 'pt', format: 'letter', compress: true });
    const fonts = await loadMemoraDocxFontData();
    fonts.forEach(([style, file, data]) => {
        doc.addFileToVFS(file, data);
        doc.addFont(file, MEMORA_DOCX_FONT_FAMILY, style);
    });
    doc.setProperties({ title, creator: 'Memoraful' });
    const writer = createMemoraDocxPdfWriter(doc);
    const headingSizes = [0, 19, 16, 13.5, 12.5, 11.5, 11];
    for (const block of blocks) {
        if (block.type === 'heading') {
            writer.paragraph(block.runs, { size: headingSizes[block.level] || 12, bold: true, before: block.level <= 2 ? 8 : 4, after: 4 });
        } else if (block.type === 'para') {
            writer.paragraph(block.runs);
        } else if (block.type === 'item') {
            writer.paragraph(block.runs, { indent: block.depth * 18, marker: block.marker, after: 3 });
        } else if (block.type === 'image') {
            await writer.image(block.src);
        } else if (block.type === 'table') {
            writer.table(block.rows);
        } else if (block.type === 'rule') {
            writer.rule();
        }
    }
    if (!blocks.length) writer.paragraph([{ text: 'This Word document has no readable text.', italic: true }]);
    return new File([doc.output('blob')], pdfName, { type: 'application/pdf', lastModified: lastModified || Date.now() });
}

/* ---------- Import entry ---------- */

function getMemoraDocxBaseName(file) {
    return String(file?.name || 'Document').replace(/\.docx$/i, '').trim() || 'Document';
}

function notifyMemoraDocxImport(message, isError = false) {
    // The native import reports its own result first; this follows it.
    setTimeout(() => {
        const popup = window.showPopupText || window.showPopup;
        if (typeof popup === 'function') popup(message, null, isError);
    }, 2600);
}

async function convertMemoraDocxFile(file, takenNames) {
    window.MemoraRuntime?.validateStudySourceSize?.(file.size, file.name);
    const { body, colorMap } = await readMemoraDocxDocument(file);
    const blocks = collectMemoraDocxBlocks(body);
    const questions = parseMemoraDocxQuestionLines(buildMemoraDocxLines(blocks, colorMap));
    const outputs = [];
    const baseName = getMemoraDocxBaseName(file);
    let needsSource = true;

    if (isMemoraDocxQuestionBank(questions)) {
        const answered = questions.filter((question) => question.a >= 0);
        const missing = questions.length - answered.length;
        const withImages = answered.filter((question) => question.hasImage).length;
        if (answered.length) {
            const rows = answered.map(({ q, c, a, e }) => (e ? { q, c, a, e } : { q, c, a }));
            outputs.push(new File([JSON.stringify(rows)], `${baseName}.json`, { type: 'application/json', lastModified: file.lastModified || Date.now() }));
        }
        needsSource = missing > 0 || withImages > 0;
        if (missing) {
            notifyMemoraDocxImport(answered.length
                ? `${file.name}: ${answered.length} question${answered.length === 1 ? '' : 's'} became a quiz. ${missing} had no marked answer, so the document was also added as a source.`
                : `${file.name}: no answers were marked, so it was added as a source. Generate a quiz from it, or mark answers (bold, color, or "Answer: B") and import again.`);
        } else if (withImages) {
            notifyMemoraDocxImport(`${file.name}: ${withImages} question${withImages === 1 ? '' : 's'} referred to a picture, so the document was also added as a source.`);
        }
    }

    if (needsSource) {
        const pdfName = typeof getMemoraImageSourcePdfName === 'function'
            ? getMemoraImageSourcePdfName(file, takenNames)
            : `${baseName}.pdf`;
        takenNames.add(pdfName);
        outputs.push(await renderMemoraDocxPdf(blocks, baseName, pdfName, file.lastModified));
    }
    return outputs;
}

// Returns the files with every Word document replaced by what it imports as; anything else passes through.
async function convertMemoraDocxSourceFiles(files = []) {
    const list = Array.from(files || []);
    if (!list.some((file) => isMemoraDocxFile(file) || isMemoraLegacyWordFile(file))) return list;
    const takenNames = new Set(list.filter((file) => !isMemoraDocxFile(file)).map((file) => String(file?.name || '')));
    const converted = [];
    const popup = window.showPopupText || window.showPopup;
    for (const file of list) {
        if (isMemoraLegacyWordFile(file)) {
            if (typeof popup === 'function') popup(`${file.name} is an older Word file. Save it as .docx or PDF, then import again.`, null, true);
            continue;
        }
        if (!isMemoraDocxFile(file)) {
            converted.push(file);
            continue;
        }
        try {
            if (typeof window.showPopup === 'function') window.showPopup(`Reading ${file.name}...`, null, false);
            converted.push(...await convertMemoraDocxFile(file, takenNames));
        } catch (error) {
            console.warn(`Word document ${file.name} could not be converted:`, error);
            if (typeof popup === 'function') popup(`${file.name} could not be read: ${error?.message || 'unknown error'}`, null, true);
        }
    }
    return converted;
}

window.MemoraDocxImport = Object.freeze({
    isDocxFile: isMemoraDocxFile,
    convertFiles: convertMemoraDocxSourceFiles
});
